import React, { useCallback, useEffect, useState } from 'react'
import { InputBase, styled, useTheme } from '@mui/material'
import { type DebouncedFunc } from 'lodash'
import debounce from 'lodash/debounce'

import { CloseIcon } from '~assets/icons'
import { Icon } from '~components/internal'

interface SearchBarProps {
  placeholder?: string
  onSearch: (query: string) => void
  debounceTime?: number // Optional debounce time in milliseconds
}

const SearchContainer = styled('div')({
  position: 'relative',
  display: 'flex',
  alignItems: 'center',
})

const SearchInputBase = styled(InputBase)(({ theme }) => ({
  width: '100%',
  '& .MuiInputBase-input': {
    borderRadius: 4,
    border: 'none',
    boxShadow: `inset 0 0 0 1px ${theme.palette.input.default}`,
    backgroundColor: theme.palette.background.paper,
    fontSize: 16,
    width: '100%',
    height: '40px',
    padding: '0 36px',
    transition: theme.transitions.create(['box-shadow', 'background-color']),
    fontFamily: theme.typography.body2.fontFamily,
    color: theme.palette.text.primary,
    '&::placeholder': {
      color: theme.palette.text.secondary,
      opacity: 1,
    },
    '&:hover': {
      boxShadow: `inset 0 0 0 2px ${theme.palette.input.hover}`,
    },
    '&:focus': {
      border: 'none',
      boxShadow: `inset 0 0 0 2px ${theme.palette.input.hover}, 0 0 0 2px ${theme.palette.input.focus}`,
    },
  },
}))

const IconWrapper = styled('div')({
  position: 'absolute',
  top: '50%',
  transform: 'translateY(-50%)',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  width: '36px',
  height: '36px',
  cursor: 'pointer',
  zIndex: 1,
})

const SearchIconWrapper = styled(IconWrapper)({
  left: 0,
})

const ClearIconWrapper = styled(IconWrapper)({
  right: 0,
})

const SearchBar: React.FC<SearchBarProps> = ({
  placeholder = 'Search',
  onSearch,
  debounceTime,
  ...props
}) => {
  const [query, setQuery] = useState<string>('')
  const theme = useTheme()

  const debouncedSearch = useCallback(
    debounceTime ? debounce((value: string) => onSearch(value), debounceTime) : onSearch,
    [onSearch, debounceTime],
  ) as DebouncedFunc<(query: string) => void>

  useEffect(() => {
    return (): void => {
      if (debounceTime && debouncedSearch.cancel) {
        debouncedSearch.cancel()
      }
    }
  }, [debouncedSearch, debounceTime])

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>): void => {
    const newValue = e.target.value
    setQuery(newValue)
    debouncedSearch(newValue)
  }

  const handleSearch = (): void => {
    onSearch(query)
  }

  const handleKeyPress = (e: React.KeyboardEvent<HTMLInputElement>): void => {
    if (e.key === 'Enter') {
      handleSearch()
    }
  }

  const clearInput = (): void => {
    setQuery('')
    onSearch('')
    if (debounceTime && debouncedSearch.cancel) {
      debouncedSearch.cancel()
    }
  }

  return (
    <SearchContainer>
      <SearchIconWrapper onClick={handleSearch}>
        <Icon name='search' size={22} color={theme.palette.text.secondary} />
      </SearchIconWrapper>
      <SearchInputBase
        value={query}
        onChange={handleInputChange}
        onKeyDown={handleKeyPress}
        placeholder={placeholder}
        fullWidth
        endAdornment={
          query && (
            <ClearIconWrapper onClick={clearInput}>
              <CloseIcon width={12} height={12} color={theme.palette.text.secondary} />
            </ClearIconWrapper>
          )
        }
        {...props}
      />
    </SearchContainer>
  )
}

export default SearchBar
